import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useInfiniteQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  Printer,
  Search,
  ChevronDown,
  FileText,
  Users,
  Check,
  Loader2,
} from "lucide-react";
import * as PosIcons from "@/assets/features/positions";
import Dropdown from "@/components/shared/inputs/DropDown";
import Wrapper from "@/components/shared/Wrapper";
import { useUser, useToken } from "@/stores/authStore";
import { fetchClient } from "@/api/fetchClient";
import { useAgencyClientsSearch } from "@/features/clientsBoard/queriesHttps/agencyClientsApi";
import { normalizeClientRow } from "@/features/clientsBoard/utils/normalizeClientRow";
import ExportModal from "@/components/shared/comman/modals/ExportModal";
import { toastSuccess } from "@/lib/Toast";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_POSITIONS_ORG_FILTER_CHANGED_EVENT,
  DASHBOARD_POSITIONS_ORG_FILTER_KEY,
} from "@/features/dashboard/constants/dashboardSessionKeys";
import { buildCandidateJobLink } from "@/utils/helpers";

function buildDashboardPositionStatusTabs(t) {
  return [
    { label: t("dashboardPage.positionsTable.tabs.active"), value: "active" },
    { label: t("dashboardPage.positionsTable.tabs.completed"), value: "closed" },
    { label: t("dashboardPage.positionsTable.tabs.draft"), value: "draft" },
  ];
}

const STAGE_COLORS = ["#076698", "#6EB2DB", "#FF9500", "#1BD43D"];
const STAGE_TRACK_BG = "rgba(138, 147, 168, 0.35)";

const MIN_SEGMENT_PCT = 6;

/** Dashboard table: one request per page (cursor afterId). */
const DASHBOARD_POSITIONS_PAGE_SIZE = 5;

function readDashboardSearchTotal(body) {
  const raw =
    body?.total ?? body?.Total ?? body?.total_count ?? body?.totalCount;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Cursor for next infinite page (`after_id` / `afterId` from positions search payload). */
function readDashboardSearchAfterId(body) {
  const v = body?.afterId ?? body?.after_id ?? null;
  return v !== undefined && v !== "" ? v : null;
}

function dashboardSearchIndicatesMorePages(body, loadedCountAcrossPages, lastItemsLen) {
  const flagged = Boolean(body?.hasNext ?? body?.has_next);
  const total = readDashboardSearchTotal(body);
  const hasMoreFromTotal =
    typeof total === "number" &&
    total > 0 &&
    loadedCountAcrossPages < total &&
    lastItemsLen > 0;
  return flagged || hasMoreFromTotal;
}

/** Scroll areas stay usable but hide scrollbars (Firefox / IE legacy / WebKit). */
const SCROLL_AREA_HIDE_SCROLLBAR =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

/** Readable label for a position stage_type value (API stores lowercase phrases). */
function formatStageTypeOptionLabel(stageType) {
  if (!stageType || typeof stageType !== "string") return "";
  return stageType
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function useDebounced(value, ms) {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
}

function fmtDashboardDate(val) {
  if (!val) return "-";
  try {
    const raw = typeof val === "string" ? val.slice(0, 10) : val;
    return format(parseISO(raw), "M-dd-yyyy");
  } catch {
    return typeof val === "string" ? val : "-";
  }
}

/** Per-stage candidate/application counts for dashboard positions (search API uses applicationsCount). */
function normalizeStagesForDisplay(stages) {
  if (!Array.isArray(stages) || stages.length === 0) return [];
  return stages.map((s) => {
    let count = 0;
    if (typeof s.applicationsCount === "number") {
      count = s.applicationsCount;
    } else if (typeof s.applications_count === "number") {
      count = s.applications_count;
    } else if (Array.isArray(s.candidates)) {
      count = s.candidates.length;
    } else if (Array.isArray(s.applications)) {
      count = s.applications.length;
    }
    return { ...s, count };
  });
}

/** Authoritative total when backend sends totalApplications; else sum stage counts. */
function totalApplicationsForPosition(position) {
  const raw =
    position?.totalApplications ?? position?.total_applications;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  const stages = normalizeStagesForDisplay(position?.stages);
  return stages.reduce((acc, s) => acc + (Number(s.count) || 0), 0);
}

function StageBar({ stages }) {
  const { t } = useTranslation();
  const items = normalizeStagesForDisplay(stages);
  if (items.length === 0) {
    return (
      <div
        className="rounded-full h-2 w-[168px] shrink-0"
        style={{ background: STAGE_TRACK_BG }}
      />
    );
  }
  const total = items.reduce((a, s) => a + (s.count || 0), 0);
  const barMinWidth = Math.max(168, items.length * 28);
  return (
    <div
      className={`max-w-[200px] overflow-x-auto shrink-0 ${SCROLL_AREA_HIDE_SCROLLBAR}`}
    >
      <div
        className="flex rounded-full overflow-hidden h-2"
        style={{
          background: STAGE_TRACK_BG,
          minWidth: barMinWidth,
        }}
        role="img"
        aria-label={t("dashboardPage.positionsTable.ariaPipelineDistribution")}
      >
        {items.map((s, i) => {
          let pct;
          if (total > 0) {
            pct = Math.max(
              (s.count / total) * 100,
              s.count ? MIN_SEGMENT_PCT : 0,
            );
          } else {
            pct = 100 / items.length;
          }
          return (
            <div
              key={s.id ?? i}
              className="h-full shrink-0 transition-[width] duration-300"
              style={{
                width: `${pct}%`,
                background: STAGE_COLORS[i % STAGE_COLORS.length],
                minWidth: s.count ? 4 : 0,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function StageDots({ stages }) {
  const { t } = useTranslation();
  const items = normalizeStagesForDisplay(stages);
  if (items.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground mt-1 leading-none">
        -
      </div>
    );
  }
  return (
    <div
      className={`flex items-center gap-x-2 gap-y-0.5 mt-1.5 text-[11px] font-medium text-[#333333] max-w-[220px] overflow-x-auto ${SCROLL_AREA_HIDE_SCROLLBAR}`}
    >
      {items.map((s, i) => {
        const count = s.count ?? 0;
        const name =
          (s.stageName ?? s.stage_name ?? "").trim() ||
          formatStageTypeOptionLabel(s.stageType ?? s.stage_type ?? "");
        return (
          <span
            key={s.id ?? i}
            className="inline-flex items-center gap-1 shrink-0 tabular-nums"
            aria-label={
              name
                ? t("dashboardPage.positionsTable.stageDotAriaNamed", {
                    name,
                    count,
                  })
                : t("dashboardPage.positionsTable.stageDotAriaUnnamed", {
                    stageNumber: i + 1,
                    count,
                  })
            }
          >
            <span
              className="inline-block rounded-full shrink-0"
              style={{
                width: 7,
                height: 7,
                background: STAGE_COLORS[i % STAGE_COLORS.length],
              }}
              aria-hidden
            />
            <span>{count}</span>
          </span>
        );
      })}
    </div>
  );
}

function buildPositionsSearchPath(companyId, isAgency, agencyId) {
  if (companyId && isAgency && agencyId) {
    return `companies/${companyId}/search/agencies/${agencyId}?resource=positions`;
  }
  return `companies/${companyId}/search?resource=positions`;
}

function PositionRowActions({ position }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDraft = position.jobStatus === "draft";
  const isArchive = position.jobStatus === "closed";

  const copyExternalLink = useCallback(() => {
    const link = position?.invitationLink;
    if (!link) return;
    navigator.clipboard.writeText(buildCandidateJobLink(link));
    toastSuccess({ header: t("dashboardPage.positionsTable.toastLinkCopied") });
  }, [position?.invitationLink, t]);

  const handleSelect = useCallback(
    (item) => {
      switch (item.value) {
        case "view":
          navigate(`/positions/stages/${position.id}`);
          break;
        case "update":
          navigate(`/positions/${position.id}`);
          break;
        case "upload_candidates_to_position":
          navigate(`/positions/stages/${position.id}`);
          break;
        case "duplicate_position":
          navigate(`/positions/new?duplicate&id=${position.id}`);
          break;
        case "copy_external":
          copyExternalLink();
          break;
        case "archive":
          navigate(`/positions/${position.id}`);
          break;
        case "extend_position_duration":
          navigate(`/positions/${position.id}`);
          break;
        case "delete":
          navigate(`/positions/${position.id}`);
          break;
        default:
          break;
      }
    },
    [navigate, position.id, copyExternalLink],
  );

  const actionItems = useMemo(() => {
    const items = [];

    if (!isDraft) {
      items.push({
        label: (
          <span className="flex items-center gap-2 cursor-pointer">
            <PosIcons.Eye
              className="w-5 h-5 shrink-0"
              color="var(--color-neutral-400)"
            />
            {t("positionsPage.rowActions.view")}
          </span>
        ),
        value: "view",
      });
    }

    if (!isArchive) {
      items.push({
        label: (
          <span className="flex items-center gap-3 cursor-pointer">
            <PosIcons.Update className="w-4 h-4 shrink-0" />{" "}
            {t("positionsPage.rowActions.update")}
          </span>
        ),
        value: "update",
      });
    }

    items.push({
      label: (
        <span className="flex items-center gap-3 cursor-pointer">
          <PosIcons.Upload className="w-4 h-4 shrink-0" />{" "}
          {t("positionsPage.rowActions.uploadCandidates")}
        </span>
      ),
      value: "upload_candidates_to_position",
    });

    items.push({
      label: (
        <span className="flex items-center gap-3 cursor-pointer">
          <PosIcons.Copy
            className="w-4 h-4 shrink-0"
            color="var(--color-neutral-400)"
          />
          {t("positionsPage.rowActions.duplicatePosition")}
        </span>
      ),
      value: "duplicate_position",
    });

    if (!isArchive && !isDraft) {
      items.push({
        label: (
          <span className="flex items-center gap-3 cursor-pointer">
            <PosIcons.CopyExternal className="w-4 h-4 shrink-0" />{" "}
            {t("positionsPage.rowActions.copyExternalLink")}
          </span>
        ),
        value: "copy_external",
      });
    }

    if (!isArchive && !isDraft) {
      items.push({
        label: (
          <span className="flex items-center gap-3 cursor-pointer">
            <PosIcons.Archive className="w-4 h-4 shrink-0" />{" "}
            {t("positionsPage.rowActions.archivePosition")}
          </span>
        ),
        value: "archive",
      });
    }

    if (isDraft) {
      items.push({
        label: (
          <span className="flex items-center gap-3 cursor-pointer text-destructive-dark">
            <PosIcons.Delete className="w-4 h-4 shrink-0" />{" "}
            {t("positionsPage.rowActions.delete")}
          </span>
        ),
        value: "delete",
      });
    }

    if (!isDraft) {
      items.push({
        label: (
          <span className="flex items-center gap-3 cursor-pointer">
            <PosIcons.Extend className="w-4 h-4 shrink-0" />{" "}
            {t("positionsPage.rowActions.extendDuration")}
          </span>
        ),
        value: "extend_position_duration",
      });
    }

    return items;
  }, [isDraft, isArchive, t]);

  return (
    <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
      <Dropdown
        label={
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer rounded-md p-1 hover:bg-muted/60 transition-colors"
            aria-label={t("dashboardPage.positionsTable.ariaPositionActions")}
          >
            <PosIcons.More className="w-4 h-4 rotate-90 text-[var(--color-neutral-400)]" />
          </button>
        }
        items={actionItems}
        onSelect={handleSelect}
        className="py-1"
        contentProps={{ align: "end" }}
      />
    </div>
  );
}

const MAX_ORG_PILLS = 8;

/** Partial query key prefix for cancelling all dashboard infinite list variants (any tab/filter). */
const DASHBOARD_POSITIONS_QUERY_ROOT = ["dashboard-positions"];

const DashboardPositionsTable = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const statusTabs = useMemo(() => buildDashboardPositionStatusTabs(t), [t]);
  const navigate = useNavigate();
  const user = useUser();
  const token = useToken();
  const companyId = user?.companyId;
  const agencyId = user?.agencyId;
  const isAgency = Boolean(user?.isAgency);

  const [activeTab, setActiveTab] = useState("active");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounced(searchInput, 350);
  const [orgFilter, setOrgFilter] = useState(() => {
    if (typeof window === "undefined") return "all";
    try {
      const raw = sessionStorage.getItem(
        DASHBOARD_POSITIONS_ORG_FILTER_KEY,
      );
      return raw && raw.trim() !== "" ? raw.trim() : "all";
    } catch {
      return "all";
    }
  });
  const [stageFilter, setStageFilter] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!statusTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(statusTabs[0].value);
    }
  }, [activeTab, statusTabs]);

  useEffect(() => {
    try {
      sessionStorage.setItem(DASHBOARD_POSITIONS_ORG_FILTER_KEY, orgFilter);
      window.dispatchEvent(new Event(DASHBOARD_POSITIONS_ORG_FILTER_CHANGED_EVENT));
    } catch {
      /* ignore quota / privacy mode */
    }
  }, [orgFilter]);

  const scrollRootRef = useRef(null);
  const loadSentinelRef = useRef(null);
  /** Prevents infinite-scroll observer from prefetching extra pages until the user scrolls the list (observer + wide rootMargin would treat the sentinel as visible when fewer than a full viewport of rows exists). */
  const userHasScrolledListRef = useRef(false);
  const intersectionGuardRef = useRef({
    hasNextPage: false,
    isFetchingNextPage: false,
  });

  const ready = Boolean(token && companyId && (!isAgency || agencyId));

  const positionsPath = useMemo(
    () => buildPositionsSearchPath(companyId, isAgency, agencyId),
    [companyId, isAgency, agencyId],
  );

  /** Same filters for table + tab totals so counts match the list (search / org / stage). */
  const dashboardPositionsFilterParams = useMemo(() => {
    const params = {};
    const q = debouncedSearch.trim();
    if (q) params["title__ilike"] = q;
    if (isAgency && orgFilter && orgFilter !== "all") {
      params.organizationId = orgFilter;
    }
    if (stageFilter && stageFilter !== "all") {
      params["stages.stage_type"] = stageFilter;
    }
    return params;
  }, [debouncedSearch, isAgency, orgFilter, stageFilter]);

  /** Key shape: [0]=scope, … [4]=jobStatus tab, [5]=debounced search, [6]=org, [7]=stage filter. */
  const dashboardPositionsQueryKey = useMemo(
    () => [
      "dashboard-positions",
      companyId,
      agencyId,
      isAgency,
      activeTab,
      debouncedSearch,
      orgFilter,
      stageFilter,
    ],
    [
      companyId,
      agencyId,
      isAgency,
      activeTab,
      debouncedSearch,
      orgFilter,
      stageFilter,
    ],
  );

  useEffect(() => {
    userHasScrolledListRef.current = false;
  }, [dashboardPositionsQueryKey]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: dashboardPositionsQueryKey,
    enabled: ready,
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const params = {
        jobStatus: activeTab,
        pageSize: DASHBOARD_POSITIONS_PAGE_SIZE,
        ...dashboardPositionsFilterParams,
      };
      if (pageParam != null && pageParam !== "") params.afterId = pageParam;

      const qs = new URLSearchParams(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      ).toString();

      const sep = positionsPath.includes("?") ? "&" : "?";
      const res = await fetchClient(`${positionsPath}${sep}${qs}`);
      return res;
    },
    getNextPageParam: (lastPage, allPages) => {
      const body = lastPage?.data ?? {};
      const loaded = allPages.reduce(
        (acc, pg) => acc + ((pg?.data?.items ?? []).length),
        0,
      );
      const lastLen = (lastPage?.data?.items ?? []).length;
      const hasNext = dashboardSearchIndicatesMorePages(body, loaded, lastLen);
      const afterId = readDashboardSearchAfterId(body);
      if (!hasNext || afterId == null) return undefined;
      return afterId;
    },
  });

  intersectionGuardRef.current = { hasNextPage, isFetchingNextPage };

  const flatPositions = useMemo(
    () => data?.pages.flatMap((p) => p?.data?.items ?? []) ?? [],
    [data?.pages],
  );

  /** Stage names in first-seen order (across loaded rows) for legend colors. */
  const pipelineLegendLabels = useMemo(() => {
    const ordered = [];
    const seen = new Set();
    for (const pos of flatPositions) {
      for (const s of pos.stages || []) {
        const sid = s.id;
        const key =
          sid != null && sid !== ""
            ? String(sid)
            : `${s.stageType ?? s.stage_type ?? ""}-${s.stageName ?? s.stage_name ?? ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const label =
          (s.stageName ?? s.stage_name ?? "").trim() ||
          formatStageTypeOptionLabel(s.stageType ?? s.stage_type ?? "");
        if (!label) continue;
        ordered.push(label);
        if (ordered.length >= 14) return ordered;
      }
    }
    return ordered;
  }, [flatPositions]);

  const stageTypeOptions = useMemo(() => {
    const labelByType = new Map();
    flatPositions.forEach((p) => {
      (p.stages || []).forEach((s) => {
        const stageType = (s?.stageType ?? s?.stage_type ?? "").trim();
        if (!stageType || labelByType.has(stageType)) return;
        const name = (s?.stageName ?? s?.stage_name ?? "").trim();
        labelByType.set(
          stageType,
          name || formatStageTypeOptionLabel(stageType),
        );
      });
    });
    /* Keep the active filter when the API omits `stages` on rows. */
    if (
      stageFilter &&
      stageFilter !== "all" &&
      !labelByType.has(stageFilter)
    ) {
      labelByType.set(stageFilter, formatStageTypeOptionLabel(stageFilter));
    }
    const rows = [...labelByType.entries()].sort((a, b) =>
      a[1].localeCompare(b[1]),
    );
    return [
      { value: "all", label: t("dashboardPage.positionsTable.allStages") },
      ...rows.map(([value, label]) => ({ value, label })),
    ];
  }, [flatPositions, stageFilter, t]);

  const tabCountQueries = useQueries({
    queries: statusTabs.map((tab) => ({
      queryKey: [
        "dashboard-positions-tab-total",
        companyId,
        agencyId,
        isAgency,
        tab.value,
        debouncedSearch,
        orgFilter,
        stageFilter,
      ],
      enabled: ready,
      queryFn: async () => {
        const params = {
          jobStatus: tab.value,
          pageSize: 1,
          ...dashboardPositionsFilterParams,
        };
        const qs = new URLSearchParams(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== null && v !== "",
          ),
        ).toString();
        const sep = positionsPath.includes("?") ? "&" : "?";
        const res = await fetchClient(`${positionsPath}${sep}${qs}`);
        return res;
      },
      select: (res) => {
        const body = res?.data;
        const raw =
          body?.total ?? body?.Total ?? body?.total_count ?? body?.totalCount;
        return typeof raw === "number" && !Number.isNaN(raw) ? raw : null;
      },
      staleTime: 60_000,
    })),
  });

  const { data: clientsSearch, isLoading: clientsLoading } =
    useAgencyClientsSearch({
      companyId,
      agencyId,
      filterKey: "active",
      page: 1,
      pageSize: 120,
      enabled: Boolean(isAgency && companyId && agencyId),
    });

  const clientRows = useMemo(() => {
    const items = clientsSearch?.data?.items ?? [];
    return items.map((item) => normalizeClientRow(item));
  }, [clientsSearch?.data?.items]);

  const totalClients = clientsSearch?.data?.total ?? clientRows.length;
  const visibleClients = clientRows.slice(0, MAX_ORG_PILLS);
  const overflowClients = Math.max(0, totalClients - MAX_ORG_PILLS);

  const stageTriggerLabel =
    stageTypeOptions.find((o) => o.value === stageFilter)?.label ??
    t("dashboardPage.positionsTable.allStages");

  const stageDropdownItems = useMemo(
    () =>
      stageTypeOptions.map((o) => {
        const selected = o.value === stageFilter;
        return {
          label: (
            <span className="flex items-center gap-3 cursor-pointer text-sm font-medium text-foreground">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {selected ? (
                  <Check
                    className="h-4 w-4 text-primary"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                ) : null}
              </span>
              {o.label}
            </span>
          ),
          value: o.value,
        };
      }),
    [stageTypeOptions, stageFilter],
  );

  const showOrgRow = isAgency && companyId && agencyId;

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || !hasNextPage) return undefined;

    let obs = null;
    let cancelled = false;

    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;
      const target = loadSentinelRef.current;
      if (!target || !scrollRootRef.current) return;

      obs = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((e) => e.isIntersecting);
          const { hasNextPage: hn, isFetchingNextPage: fetching } =
            intersectionGuardRef.current;
          if (
            !hit ||
            !hn ||
            fetching ||
            !userHasScrolledListRef.current
          ) {
            return;
          }
          fetchNextPage();
        },
        {
          root: scrollRootRef.current,
          rootMargin: "72px",
          threshold: 0,
        },
      );
      obs.observe(target);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      obs?.disconnect();
    };
  }, [hasNextPage, fetchNextPage, activeTab]);

  return (
    <Wrapper className="p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground">{t("dashboardPage.positionsTable.sectionTitle")}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            className="flex items-center gap-1.5 text-xs text-secondary-foreground border border-[#E5E7EB] px-3 py-1.5 rounded-lg hover:bg-muted/40 transition-colors bg-white shadow-light-sm"
          >
            <Printer size={13} />
            {t("common.tableToolbar.exportAs")}
            <ChevronDown size={12} />
          </button>

          <Dropdown
            label={
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-secondary-foreground border border-[#E5E7EB] px-3 py-1.5 rounded-lg hover:bg-muted/40 transition-colors bg-white shadow-light-sm max-w-[220px]"
                aria-label={t("dashboardPage.positionsTable.ariaFilterStage", { stage: stageTriggerLabel })}
              >
                <PosIcons.Filter
                  className="w-4 h-4 shrink-0"
                  color="var(--color-neutral-400)"
                />
                <span className="truncate font-medium">{stageTriggerLabel}</span>
                <ChevronDown size={12} className="shrink-0 opacity-70" />
              </button>
            }
            items={stageDropdownItems}
            onSelect={(item) => setStageFilter(item.value)}
            contentProps={{
              align: "end",
              className: `min-w-[14rem] max-h-[min(280px,50vh)] overflow-y-auto overflow-x-hidden ${SCROLL_AREA_HIDE_SCROLLBAR}`,
            }}
          />

          <div className="flex items-center gap-1.5 border border-[#E5E7EB] px-3 py-1.5 rounded-lg bg-background min-w-[200px] flex-1 sm:flex-initial sm:max-w-xs shadow-light-sm">
            <Search size={13} className="text-muted-foreground shrink-0" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("dashboardPage.positionsTable.placeholderSearchPositions")}
              className="text-xs outline-none bg-transparent w-full placeholder:text-muted-foreground"
              aria-label={t("dashboardPage.positionsTable.ariaSearchPositions")}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-[#F0F0F0] mb-4">
        {statusTabs.map((tab, idx) => {
          const count = tabCountQueries[idx]?.data;
          const suffix =
            typeof count === "number" ? ` (${count})` : "";
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => {
                if (tab.value === activeTab) return;
                /*
                 * Abort only this tab key's infinite fetches. Cancelling every `dashboard-*` prefix
                 * variant was disrupting other caches and leftover `has_next` signalling for scroll paging.
                 */
                void queryClient.cancelQueries({
                  predicate: ({ queryKey: k }) =>
                    Array.isArray(k) &&
                    k[0] === DASHBOARD_POSITIONS_QUERY_ROOT[0] &&
                    k[4] === activeTab,
                });
                setActiveTab(tab.value);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.value
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              {suffix}
            </button>
          );
        })}
      </div>

      {showOrgRow ? (
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            {t("dashboardPage.positionsTable.orgFilterHeading")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setOrgFilter("all")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                orgFilter === "all"
                  ? "bg-[#D8EBF7] text-primary border border-[#D8EBF7]"
                  : "border border-border text-foreground hover:bg-muted/50"
              }`}
            >
              {t("dashboardPage.positionsTable.allClients")}
            </button>
            {clientsLoading && (
              <span className="text-xs text-muted-foreground">{t("dashboardPage.positionsTable.loading")}</span>
            )}
            {!clientsLoading &&
              visibleClients.map((row) => {
                const orgKey =
                  row.organizationId ?? row.companyId ?? row.id ?? "";
                const selected = orgFilter === orgKey;
                const logo =
                  row.logo ?? row.companyLogo ?? row.organizationLogo;
                const label =
                  row.companyName || row.organizationName || row.name || "-";
                return (
                  <button
                    key={orgKey || row.id}
                    type="button"
                    onClick={() => setOrgFilter(orgKey)}
                    className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors max-w-[200px] ${
                      selected
                        ? "bg-[#D8EBF7] text-primary border-[#D8EBF7]"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    {logo ? (
                      <img
                        src={logo}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover shrink-0 bg-white"
                      />
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] shrink-0">
                        {(label || "?").slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            {!clientsLoading && overflowClients > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-muted-foreground/40 text-muted-foreground">
                +{overflowClients}
              </span>
            )}
          </div>
        </div>
      ) : null}

      <div
        className={`max-[840px]:overflow-x-auto max-[840px]:w-full ${SCROLL_AREA_HIDE_SCROLLBAR}`}
      >
        <div
          ref={scrollRootRef}
          onScroll={(e) => {
            if (e.currentTarget.scrollTop > 4) {
              userHasScrolledListRef.current = true;
            }
          }}
          className={`max-h-[min(520px,62vh)] overflow-y-auto overflow-x-visible max-[840px]:overflow-x-visible min-w-0 ${SCROLL_AREA_HIDE_SCROLLBAR}`}
        >
          <table className="w-full text-sm min-w-[840px]">
            <thead className="sticky top-0 z-[1] bg-card shadow-[0_1px_0_0_var(--border)]">
              <tr className="text-xs font-medium text-muted-foreground">
                <th className="text-left pb-3 pl-2 whitespace-nowrap">
                  {t("dashboardPage.positionsTable.cols.jobTitle")}
                </th>
                <th className="text-left pb-3 whitespace-nowrap">
                  {t("dashboardPage.positionsTable.cols.jobCategory")}
                </th>
                <th className="text-left pb-3 whitespace-nowrap">
                  {t("dashboardPage.positionsTable.cols.applications")}
                </th>
                <th className="text-left pb-3 whitespace-nowrap min-w-[180px]">
                  {t("dashboardPage.positionsTable.cols.stages")}
                </th>
                <th className="text-left pb-3 whitespace-nowrap">
                  {t("dashboardPage.positionsTable.cols.createdDate")}
                </th>
                <th className="text-left pb-3 whitespace-nowrap">{t("dashboardPage.positionsTable.cols.endDate")}</th>
                <th className="text-left pb-3 whitespace-nowrap">
                  {t("dashboardPage.positionsTable.cols.totalCandidates")}
                </th>
                <th className="text-left pb-3 pr-2 whitespace-nowrap w-[52px]">
                  {t("dashboardPage.positionsTable.cols.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t border-[#F8FAFC]">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="py-3 pl-2">
                        <div className="h-4 bg-muted rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!isLoading && error && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-8 text-center text-destructive text-sm"
                  >
                    {t("dashboardPage.positionsTable.errorLoad")}
                  </td>
                </tr>
              )}
              {!isLoading && !error && flatPositions.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-8 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center gap-3 max-w-md mx-auto">
                      <span>
                        {showOrgRow && orgFilter !== "all"
                          ? t(
                              "dashboardPage.positionsTable.emptyFilteredByOrganization",
                            )
                          : t("dashboardPage.positionsTable.empty")}
                      </span>
                      {showOrgRow &&
                      orgFilter !== "all" &&
                      !clientsLoading ? (
                        <button
                          type="button"
                          className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
                          onClick={() => setOrgFilter("all")}
                        >
                          {t(
                            "dashboardPage.positionsTable.clearOrgFilterShowAll",
                            {
                              label: t(
                                "dashboardPage.positionsTable.allClients",
                              ),
                            },
                          )}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                flatPositions.map((pos) => {
                  const totalApplicants = totalApplicationsForPosition(pos);
                  const category =
                    pos.field?.trim() ||
                    pos.company?.industry?.trim() ||
                    "-";

                  return (
                    <tr
                      key={pos.id}
                      className="border-t border-[#F8FAFC] hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/positions/stages/${pos.id}`)}
                    >
                      <td className="py-3 pl-2 align-top">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-7 h-7 rounded-md bg-[#EBF5FF] flex items-center justify-center">
                            <FileText size={14} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground text-xs leading-tight truncate max-w-[220px]">
                              {pos.title}
                            </p>
                            <p className="text-xs text-muted-foreground leading-tight truncate max-w-[220px]">
                              {pos.company?.companyName || "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                        {category}
                      </td>
                      <td className="py-3 align-top">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs bg-[#DCFCE7] text-[#16A34A] font-semibold whitespace-nowrap">
                          <Users size={14} className="shrink-0 opacity-90" />
                          <span>
                            {t("dashboardPage.positionsTable.applicationsLine", {
                              countDisplay: totalApplicants > 99 ? "99+" : String(totalApplicants),
                            })}
                          </span>
                        </span>
                      </td>
                      <td className="py-3 align-top">
                        <StageBar stages={pos.stages} />
                        <StageDots stages={pos.stages} />
                      </td>
                      <td className="py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                        {fmtDashboardDate(pos.createdAt)}
                      </td>
                      <td className="py-3 text-xs text-muted-foreground whitespace-nowrap align-top">
                        {fmtDashboardDate(pos.endDate)}
                      </td>
                      <td className="py-3 text-xs font-medium tabular-nums align-top">
                        {totalApplicants.toLocaleString()}
                      </td>
                      <td className="py-3 pr-2 align-top">
                        <PositionRowActions position={pos} />
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && hasNextPage && (
                <tr className="border-0">
                  <td
                    colSpan={8}
                    className={
                      isFetchingNextPage
                        ? "py-2 text-center align-middle"
                        : "p-0 text-center align-middle"
                    }
                  >
                    {isFetchingNextPage ? (
                      <span className="text-xs text-muted-foreground">
                        {t("dashboardPage.positionsTable.loadingMore")}
                      </span>
                    ) : null}
                    <div
                      ref={loadSentinelRef}
                      className="h-1 w-full min-h-[2px] pointer-events-none shrink-0"
                      aria-hidden
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!isLoading && hasNextPage ? (
        <div className="flex justify-center mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-5 py-2 text-xs font-semibold text-primary shadow-light-sm hover:bg-muted/40 disabled:opacity-60 disabled:pointer-events-none transition-colors"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                {t("dashboardPage.positionsTable.loading")}
              </>
            ) : (
              t("dashboardPage.positionsTable.showMore")
            )}
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-4 pt-4 border-t border-[#F0F0F0] px-2 py-2 rounded-lg bg-muted/20">
        {pipelineLegendLabels.length === 0 ? (
          <span className="text-[11px] text-[#909FBA] font-medium">
            {t("dashboardPage.positionsTable.pipelineLegendHint")}
          </span>
        ) : (
          pipelineLegendLabels.map((label, i) => (
            <span
              key={`${label}-${i}`}
              className="flex items-center gap-1.5 max-w-[140px]"
            >
              <span
                className="inline-block rounded-full shrink-0"
                style={{
                  width: 8,
                  height: 8,
                  background: STAGE_COLORS[i % STAGE_COLORS.length],
                }}
              />
              <span className="text-[11px] text-[#909FBA] font-medium truncate">
                {label}
              </span>
            </span>
          ))
        )}
      </div>

      <ExportModal open={exportOpen} onOpenChange={setExportOpen} />
    </Wrapper>
  );
};

export default DashboardPositionsTable;
